import type { MetricsBackfillResult } from '../../api/types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapPaymentError, PAYMENT_MESSAGES } from '../../api/endpoints/payments';
import { addCalendarDays, istDateInput } from '../payments/paymentDates';
import { apiRange, rangeError } from './metricsFormat';
import { useBackfillPaymentMetrics } from './metricsHooks';
import styles from './PaymentsMetricsTab.module.css';
import { Button } from '../../components/ui/Button';
import buttonStyles from '../settings/SettingsView.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { DialogHelp } from '../../help/DialogHelp';

export function BackfillHistoryDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const yesterday = addCalendarDays(istDateInput(), -1);
  const [from, setFrom] = useState(addCalendarDays(yesterday, -6)),
    [end, setEnd] = useState(yesterday);
  const mutation = useBackfillPaymentMetrics();
  const running = useRef(false);
  const [phase, setPhase] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [submitted, setSubmitted] = useState<{ from: string; end: string } | null>(null);
  const start = () => {
    if (running.current || phase !== 'idle' || error) return;
    running.current = true;
    setSubmitted({ from, end });
    setPhase('running');
    mutation.mutate(apiRange(from, end), {
      onSuccess: () => {
        running.current = false;
        setPhase('success');
      },
      onError: () => {
        running.current = false;
        setPhase('error');
      },
    });
  };
  const restart = () => {
    mutation.reset();
    setSubmitted(null);
    setPhase('idle');
  };
  const failureCode = (mutation.error as { code?: keyof typeof PAYMENT_MESSAGES } | null)?.code;
  const failureMessage =
    failureCode && Object.prototype.hasOwnProperty.call(PAYMENT_MESSAGES, failureCode)
      ? mapPaymentError(mutation.error)
      : 'Could not complete backfill. Review the range before trying again.';
  const error =
    rangeError(from, end) ?? (end > yesterday ? 'History must end no later than yesterday.' : null);
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="backfill-title"
        className={styles.dialog}
      >
        <div className={styles.dialogTitleRow}>
          <h2 id="backfill-title">{t('analytics.backfillHistoryDialog.title')}</h2>
          <DialogHelp context="backfill-history" title={t('analytics.backfillHistoryDialog.title')} />
        </div>
        <p>{t('analytics.backfillHistoryDialog.intro')}</p>
        {(phase === 'idle' || phase === 'running') && (
          <div className={styles.filters}>
            <label>
              {t('analytics.backfillHistoryDialog.from')}
              <DatePicker
                value={from}
                max={end}
                disabled={phase === 'running'}
                onChange={setFrom}
              />
            </label>
            <label>
              {t('analytics.backfillHistoryDialog.through')}
              <DatePicker
                value={end}
                min={from}
                max={yesterday}
                disabled={phase === 'running'}
                onChange={setEnd}
              />
            </label>
          </div>
        )}
        {error && <p role="alert">{error}</p>}
        {phase === 'error' && (
          <div role="alert" className={styles.backfillError}>
            <h3>{t('analytics.backfillHistoryDialog.failedTitle')}</h3>
            <p>{failureMessage}</p>
            <p>
              {t('analytics.backfillHistoryDialog.failedDetail', {
                from: submitted?.from ?? '',
                end: submitted?.end ?? '',
              })}
            </p>
          </div>
        )}
        {phase === 'running' && <p role="status">{t('analytics.backfillHistoryDialog.running')}</p>}
        {phase === 'success' && mutation.data && <BackfillCompletion result={mutation.data} />}
        <div className={styles.filters}>
          {phase === 'success' ? (
            <>
              <Button variant="secondary" size="sm" onClick={restart}>
                {t('analytics.backfillHistoryDialog.recomputeAgain')}
              </Button>
              <button
                className={`${buttonStyles.submit} ${styles.primaryButton}`}
                onClick={onClose}
              >
                {t('analytics.backfillHistoryDialog.done')}
              </button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={phase === 'running'}
                onClick={onClose}
              >
                {t('common.actions.close')}
              </Button>
              {phase === 'error' ? (
                <Button variant="secondary" size="sm" onClick={restart}>
                  {t('analytics.backfillHistoryDialog.reviewRetry')}
                </Button>
              ) : (
                <button
                  className={`${buttonStyles.submit} ${styles.primaryButton}`}
                  disabled={!!error || phase === 'running'}
                  onClick={start}
                >
                  {phase === 'running' ? 'Backfilling…' : 'Confirm backfill'}
                </button>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export function BackfillCompletion({ result }: { result: MetricsBackfillResult }) {
  const { t } = useTranslation();
  return (
    <div role="status" className={styles.backfillSuccess}>
      <h3>{t('analytics.backfillHistoryDialog.completeTitle')}</h3>
      <p>
        {t('analytics.backfillHistoryDialog.completeDetail', {
          days: result.days,
          from: result.from,
          to: addCalendarDays(result.to, -1),
        })}
      </p>
      <p>{t('analytics.backfillHistoryDialog.completeNote')}</p>
    </div>
  );
}
